import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../utils/cn';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Radix opens a tooltip on hover and on focus, and a touch screen has neither:
 * `TooltipTrigger` returns early from `onPointerMove` when `pointerType` is
 * `'touch'`, and closes again on both `onPointerDown` and `onClick`. Tapping
 * therefore never reveals the content. Spreading these props makes a tap — or a
 * click — toggle the tooltip, leaving hover and keyboard focus as they were.
 *
 *   const tapToggle = useTooltipTapToggle();
 *
 *   <Tooltip {...tapToggle.rootProps}>
 *     <TooltipTrigger asChild>
 *       <button type="button" {...tapToggle.triggerProps}>…</button>
 *     </TooltipTrigger>
 *     <TooltipContent>…</TooltipContent>
 *   </Tooltip>
 *
 * The trigger has to be a real `<button>`: React delegates click from the root
 * container, and iOS Safari does not deliver those for a bare `<span>`/`<div>`.
 */
const useTooltipTapToggle = () => {
  const [open, setOpen] = React.useState(false);

  // Radix's own pointerdown handler closes the tooltip before the click lands,
  // so by then `open` is already false and toggling it could only ever re-open.
  // Record what it was when the press started — our handler runs first. `null`
  // means no press preceded the click (keyboard activation), where `open` still
  // holds.
  const openOnPointerDown = React.useRef<boolean | null>(null);

  const onPointerDown = React.useCallback(() => {
    openOnPointerDown.current = open;
  }, [open]);

  const onClick = React.useCallback(
    (event: React.MouseEvent) => {
      // Radix runs its handlers after ours and skips them once the event is
      // default-prevented; that is what suppresses its close-on-click. A
      // `<button type="button">` has no default action to lose.
      event.preventDefault();
      const wasOpen = openOnPointerDown.current ?? open;
      openOnPointerDown.current = null;
      setOpen(!wasOpen);
    },
    [open]
  );

  return {
    rootProps: { open, onOpenChange: setOpen },
    triggerProps: { onPointerDown, onClick },
  };
};

// Colors live here rather than in a caller's `className` because `cn` is a plain
// join, not tailwind-merge: a conflicting utility passed in wins or loses on
// stylesheet order alone (`bg-white` is emitted after `bg-gray-800`, so it would
// beat it). Pick a variant instead of overriding.
const tooltipVariants = {
  light: 'bg-white text-gray-900 border-gray-200',
  dark: 'bg-gray-800 text-white border-gray-700',
};

interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  variant?: keyof typeof tooltipVariants;
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, variant = 'light', ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden rounded-md border px-3 py-1.5 text-sm shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      tooltipVariants[variant],
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  useTooltipTapToggle,
};
